/**
 * Vertrag der Designansichten (Stufe F): Liste, Vorlagen, Editor, Anlegen aus Vorlage, Kopieren,
 * Speichern mit Versionsvergleich, Vorlage wechseln, Veröffentlichen und Löschen. Die Vorschau
 * rendert die SPA selbst mit `@pat/design-html`; Prüfregeln vor dem Veröffentlichen stehen in
 * `design-checks.ts` und gelten in SPA und API gleich.
 */
import { z } from 'zod';
import { Design, DesignBox, DesignColors, DesignLink, DesignLogo, DesignTrust, EbaySiteId, IsoDateTime, Version } from '@pat/core/entities';
import { MEDIA_ID, MediaView, PreviewContext, ReviseState } from './content.js';

/** Design-IDs landen in Schlüsseln (`DESIGN#<id>`): kein `#`, kurz. */
export const DESIGN_ID = /^[A-Za-z0-9_-]{1,64}$/;
/** Kennung neuer Designs: die SPA erzeugt sie einmal je Anlege- oder Kopiervorgang (Wiederholung idempotent). */
export const NEW_DESIGN_ID = /^des-[0-9a-f]{12,32}$/;
/**
 * Kennung neuer Boxen, Links und Trust-Einträge (`b-`, `l-`, `t-`). Nie rein numerisch, damit sie
 * nicht mit `legacyId` in Element-IDs zusammenfällt, die Kunden-CSS anspricht.
 */
export const NEW_PART_ID = /^[blt]-[0-9a-f]{8,16}$/;
/** Wie `GALLERY_ID` der Galerierouten. */
const GALLERY_REF = /^[A-Za-z0-9_-]{1,40}$/;
/** Überschrift genau dieses Werts blendet der Renderer aus (Legacy-Konvention). */
export const HEADLINE_HIDDEN = 'X-PAT-HEADLINE-HIDDEN-X';
/** Obergrenze je Boxtext; Designs haben keine Auslagerung nach S3 wie Artikelblöcke. */
export const MAX_BOX_TEXT = 65_535;

export const DesignSummaryView = z.object({
  designId: z.string(),
  name: z.string(),
  version: Version,
  /** Zuletzt veröffentlichte Version; fehlt = nie veröffentlicht. */
  publishedVersion: Version.optional(),
  variant: z.number().int().min(1).max(15).optional(),
  updatedAt: IsoDateTime.optional(),
  /**
   * Nicht gelöschte Artikel, deren Entwurf dieses Design gewählt hat. Seit 30.10.2026 nicht mehr in `GET /designs`
   * (las alle Artikelköpfe, prod-Drosselung); die Zahl steht im Designdetail (`DesignEditorResponse.articleCount`).
   */
  articleCount: z.number().int().nonnegative().optional(),
});
export type DesignSummaryView = z.infer<typeof DesignSummaryView>;
export const DesignsResponse = z.object({
  designs: z.array(DesignSummaryView),
  /** Rolle und Umzugszustand erlauben Anlegen und Bearbeiten (fehlt bei älteren Antworten). */
  editable: z.boolean().optional(),
});
export type DesignsResponse = z.infer<typeof DesignsResponse>;
export const DesignDetailResponse = z.object({ design: Design, published: Design.optional(), media: z.record(z.string(), MediaView) });
export type DesignDetailResponse = z.infer<typeof DesignDetailResponse>;

/** Eigene, nicht gelöschte Galerie zur Auswahl in Galerieboxen. */
export const DesignGalleryOption = z.object({
  galleryId: z.string(),
  name: z.string(),
  site: EbaySiteId,
  /** Regeln gesperrt: der Worker hat dann keine vollständige Auswahl und lässt die Galeriebox leer (Hinweis `gallery-locked`). */
  locked: z.boolean(),
});
export type DesignGalleryOption = z.infer<typeof DesignGalleryOption>;

/** Vorlage: liefert nur Farben und Variante (wie im Altsystem); Boxen, Links und Logo kommen aus dem Grundgerüst. */
export const DesignTemplateView = z.object({
  templateId: z.string(),
  name: z.string(),
  variant: z.number().int().min(1).max(15),
  colors: DesignColors,
});
export type DesignTemplateView = z.infer<typeof DesignTemplateView>;

export const DesignTemplatesResponse = z.object({
  templates: z.array(DesignTemplateView),
  /** Eigener Shop auch in der Vorschau vor dem Anlegen; optional für ältere API-Antworten. */
  preview: PreviewContext.optional(),
  /** Vorbelegung des Anlegeformulars aus dem eBay-Namen (wie im Altsystem). */
  defaults: z.object({ name: z.string(), logoText: z.string(), copyrightText: z.string() }),
  /** Galerien für die optionale Cross-Selling-Box; leer ohne Cross-Selling. */
  galleries: z.array(DesignGalleryOption),
  /**
   * Grundgerüst eines neuen Designs mit den Vorbelegungen, ohne Galeriebox und mit sichtbarer
   * Shopnavigation; für die Vorschau setzt die SPA Farben und Variante der gewählten Vorlage ein.
   */
  skeleton: Design,
});
export type DesignTemplatesResponse = z.infer<typeof DesignTemplatesResponse>;

export const DesignEditorResponse = DesignDetailResponse.extend({
  preview: PreviewContext,
  editable: z.boolean(),
  /** Farben, Schrift, Variante und Kunden-CSS änderbar (Tarifrecht `designColors`); Vorlage wechseln geht immer. */
  colorsEditable: z.boolean(),
  /** Nicht gelöschte Artikel, deren Entwurf dieses Design gewählt hat (Löschsperre, Hinweis beim Veröffentlichen). */
  articleCount: z.number().int().nonnegative(),
  galleries: z.array(DesignGalleryOption),
  /** Nur nach dem Speichern: gespeichert, aber nicht vollständig nachladbar; die SPA lädt die Ansicht neu. */
  reloadIncomplete: z.boolean().optional(),
});
export type DesignEditorResponse = z.infer<typeof DesignEditorResponse>;

const DesignName = z.string().trim().min(1).max(120);

export const DesignCreateRequest = z
  .object({
    designId: z.string().regex(NEW_DESIGN_ID),
    templateId: z.string().regex(DESIGN_ID),
    name: DesignName,
    /** Fehlt = eBay-Name. */
    logoText: z.string().max(255).optional(),
    /** Fehlt = „Copyright <Jahr> <eBay-Name>“. */
    copyrightText: z.string().max(255).optional(),
    /** Eigene Galerie für die Box „Weitere interessante Angebote“; fehlt = keine Galeriebox. */
    galleryId: z.string().regex(GALLERY_REF).optional(),
    /** Shopkategorien anzeigen; fehlt = anzeigen wie im Altsystem (ohne Shop lässt der Worker die Box weg). */
    showStoreCategories: z.boolean().optional(),
  })
  .strict();
export type DesignCreateRequest = z.infer<typeof DesignCreateRequest>;
export const DesignCreateResponse = z.object({ designId: z.string() });
export type DesignCreateResponse = z.infer<typeof DesignCreateResponse>;

export const DesignCopyRequest = z.object({ designId: z.string().regex(NEW_DESIGN_ID) }).strict();
export type DesignCopyRequest = z.infer<typeof DesignCopyRequest>;

const Uniquely = <T extends { id: string }>(list: readonly T[]) => new Set(list.map(entry => entry.id)).size === list.length;

/**
 * Speichern des ganzen Entwurfs. `legacyId` an Boxen wird ignoriert (die API übernimmt sie aus dem
 * gespeicherten Stand); Farben normalisiert die API (`normalizeDesignColors`), `body_opacity` bleibt roh.
 */
export const DesignSaveRequest = z
  .object({
    expectedVersion: Version,
    name: DesignName,
    /** Pflicht: ohne Wert rendert der Renderer Variante 1, core sagt „Vorlage entscheidet“. */
    variant: z.number().int().min(1).max(15),
    colors: DesignColors,
    logo: DesignLogo.optional(),
    logoMediaId: z.string().regex(MEDIA_ID).optional(),
    copyrightText: z.string().max(255).optional(),
    trustAlign: z.enum(['left', 'center', 'right']),
    addCode: Design.shape.addCode,
    boxes: z.array(DesignBox.extend({ text: z.string().max(MAX_BOX_TEXT).optional() })).max(50),
    links: z.array(DesignLink.extend({ id: z.string().min(1).max(40) })).max(50),
    trust: z.array(DesignTrust.extend({ id: z.string().min(1).max(40) })).max(20),
  })
  .strict()
  .superRefine((input, ctx) => {
    const issue = (path: (string | number)[], message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    if (!Uniquely(input.boxes)) issue(['boxes'], 'Box doppelt vorhanden');
    if (!Uniquely(input.links)) issue(['links'], 'Link doppelt vorhanden');
    if (!Uniquely(input.trust)) issue(['trust'], 'Trust-Eintrag doppelt vorhanden');
    if (input.boxes.filter(box => box.type === 'article').length > 1) issue(['boxes'], 'Höchstens eine Artikelbox');
    input.boxes.forEach((box, index) => {
      if (box.galleryId !== undefined && box.type !== 'gallery') issue(['boxes', index, 'galleryId'], 'Galerie nur in Galerieboxen');
      if (box.galleryId !== undefined && !GALLERY_REF.test(box.galleryId)) issue(['boxes', index, 'galleryId'], 'Ungültige Galerie');
      if (box.shopNavi !== undefined && box.type !== 'shop-navi') issue(['boxes', index, 'shopNavi'], 'Shopkategorien nur in der Shopbox');
    });
  });
export type DesignSaveRequest = z.infer<typeof DesignSaveRequest>;

/** „Design wechseln“: übernimmt Farben und Variante der Vorlage, sonst nichts (wie im Altsystem). */
export const DesignApplyTemplateRequest = z.object({ expectedVersion: Version, templateId: z.string().regex(DESIGN_ID) }).strict();
export type DesignApplyTemplateRequest = z.infer<typeof DesignApplyTemplateRequest>;

export const DesignPublishRequest = z.object({ expectedVersion: Version }).strict();
export type DesignPublishRequest = z.infer<typeof DesignPublishRequest>;
/**
 * `revise`: ob der Worker den Abgleich der betroffenen Angebote annehmen würde. Die Absicht schreibt
 * `packages/data` beim Veröffentlichen selbst (Outbox); `none` = kein Artikel nutzt dieses Design.
 */
/** Veröffentlichen gibt nichts an bestehende Angebote weiter (Festlegung Flori, 24.10.2026). */
export const DesignPublishResponse = DesignEditorResponse;
export type DesignPublishResponse = z.infer<typeof DesignPublishResponse>;

/** `DELETE …/designs/:id?expectedVersion=<n>` (Löschen ist weich; gelöschte Designs erscheinen nicht mehr). */
export const DesignDeleteQuery = z.object({ expectedVersion: z.string().regex(/^[0-9]{1,9}$/).transform(Number) }).strict();
export const DesignDeleteResponse = z.object({ designId: z.string(), deleted: z.literal(true) });
export type DesignDeleteResponse = z.infer<typeof DesignDeleteResponse>;

type Colors = z.infer<typeof DesignColors>;
type Obj = Record<string, unknown>;
const isObj = (value: unknown): value is Obj => typeof value === 'object' && value !== null && !Array.isArray(value);

/** Altschlüssel überschreiben im Renderer die neuen; der Import bildet sie ab, neu geschrieben werden sie nie. */
const OLD_COLOR_KEYS = ['header', 'header_bg', 'col_lr', 'col_lr_bg', 'col_main', 'col_main_bg'];
const BACKGROUND_KEYS = ['body_bg', 'color_1_bg', 'color_2_bg', 'color_3_bg'];
const FONT_COLOR_KEYS = ['color_1', 'color_2', 'color_3'];

/** Hex wie das Altsystem es liest (1 Zeichen → 6×, 2 → 3×, 3 → CSS-Kurzform), geschrieben 6-stellig klein. */
export function hex6(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).trim().toLowerCase();
  if (!/^[0-9a-f]{1,6}$/.test(text)) return undefined;
  if (text.length === 1) return text.repeat(6);
  if (text.length === 2) return text.repeat(3);
  if (text.length === 3) return [...text].map(ch => ch + ch).join('');
  return text.length === 6 ? text : undefined;
}

/** Ganzzahl aus Zahl oder Ziffernstring (Altbestand speichert beides), sonst `undefined`. */
function integer(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isInteger(value) ? value : undefined;
  return typeof value === 'string' && /^-?[0-9]{1,6}$/.test(value.trim()) ? Number(value) : undefined;
}

/**
 * Farben so, wie sie geschrieben werden: Hex 6-stellig klein, `font_size` 1–3, `bg_type` 0/1/2/9,
 * `active` 0/1. Unbekannte Schlüssel (z. B. Editorzustand) bleiben, ebenso `body_opacity` als Rohwert:
 * im Altsystem nur Editorvorschau, der Renderer ignoriert es, Import und Speichern lassen es stehen
 * (Festlegung 24.10.2026). `invalid` = Pfad des ersten Fehlers.
 */
export function normalizeDesignColors(colors: Colors): { colors: Colors } | { invalid: string } {
  const out: Obj = { ...colors };
  const old = OLD_COLOR_KEYS.find(key => key in out);
  if (old) return { invalid: old };
  if (out['font_size'] !== undefined) {
    const size = integer(out['font_size']);
    if (size === undefined || size < 1 || size > 3) return { invalid: 'font_size' };
    out['font_size'] = size;
  }
  for (const key of BACKGROUND_KEYS) {
    if (out[key] === undefined) continue;
    if (!isObj(out[key])) return { invalid: key };
    const background: Obj = { ...out[key] };
    if (background['bg_type'] !== undefined) {
      const type = integer(background['bg_type']);
      if (type === undefined || ![0, 1, 2, 9].includes(type)) return { invalid: `${key}.bg_type` };
      background['bg_type'] = type;
    }
    for (const field of ['color1', 'color2', 'shadow_color']) {
      if (background[field] === undefined) continue;
      const value = hex6(background[field]);
      if (value === undefined) return { invalid: `${key}.${field}` };
      background[field] = value;
    }
    out[key] = background;
  }
  for (const key of FONT_COLOR_KEYS) {
    if (out[key] === undefined) continue;
    if (!isObj(out[key])) return { invalid: key };
    const font: Obj = { ...out[key] };
    if (font['font_color'] !== undefined) {
      const value = hex6(font['font_color']);
      if (value === undefined) return { invalid: `${key}.font_color` };
      font['font_color'] = value;
    }
    if (font['active'] !== undefined) {
      const active = integer(font['active']);
      if (active === undefined) return { invalid: `${key}.active` };
      font['active'] = active === 0 ? 0 : 1;
    }
    out[key] = font;
  }
  return { colors: out as Colors };
}
